import { motion, AnimatePresence } from "framer-motion";
import {
  X, ClipboardText, Scales, FilePdf, ArrowRight, Key, Lightning, ArrowSquareOut, Question, Rocket,
} from "@phosphor-icons/react";
import { useI18n } from "@/i18n";

const FLOW_ICONS = [ClipboardText, Scales, FilePdf];
const FLOW_COLORS = ["#0369A1", "#0EA5E9", "#10B981"];
const KEY_SOURCES = [
  { name: "Anthropic (Claude)", url: "https://console.anthropic.com/settings/keys", noteKey: "md.hiwNoteAnthropic" },
];

export function HowItWorks({ open, onClose, onStartTour }) {
  const { t } = useI18n();
  const FLOW = (t("md.hiwFlow") || []).map((f, i) => ({ ...f, Icon: FLOW_ICONS[i], color: FLOW_COLORS[i] }));
  return (
    <AnimatePresence>
      {open && (
        <motion.div className="fixed inset-0 z-[75]" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="howitworks-panel">
          <div className="absolute inset-0 bg-lc-navy/55 backdrop-blur-sm" onClick={onClose} />
          <motion.aside
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ type: "spring", stiffness: 300, damping: 32 }}
            className="absolute right-0 top-0 flex h-full w-full max-w-md flex-col border-l border-lc-ink/10 bg-white"
          >
            <div className="flex items-center justify-between border-b border-lc-ink/10 px-5 py-4">
              <h2 className="flex items-center gap-2 font-serif text-xl text-lc-ink">
                <Question size={20} weight="duotone" className="text-lc-ocean" /> {t("md.hiwTitle")}
              </h2>
              <button onClick={onClose} data-testid="howitworks-close" className="text-lc-steel hover:text-lc-ink transition-colors duration-200">
                <X size={18} />
              </button>
            </div>

            <div className="lc-scroll flex-1 overflow-y-auto p-5">
              <p className="text-sm text-lc-steel">{t("md.hiwIntro")}</p>

              {/* visual 3-step guide */}
              <div className="mt-4 space-y-2">
                {FLOW.map((f, i) => (
                  <div key={f.title}>
                    <div className="flex items-center gap-3 rounded-sm border border-lc-ink/10 p-3.5">
                      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-sm"
                        style={{ background: `${f.color}14`, border: `1px solid ${f.color}44` }}>
                        <f.Icon size={22} weight="duotone" style={{ color: f.color }} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-[10px] text-lc-steel">{t("md.hiwStep")} {i + 1}</span>
                          <h3 className="font-serif text-base text-lc-ink">{f.title}</h3>
                        </div>
                        <p className="mt-0.5 text-xs text-lc-steel">{f.body}</p>
                      </div>
                    </div>
                    {i < FLOW.length - 1 && (
                      <div className="flex justify-center py-0.5">
                        <ArrowRight size={16} weight="bold" className="rotate-90 text-lc-ink/25" />
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* BYO-Key section */}
              <div className="mt-7 rounded-sm border border-lc-ink/10 bg-lc-paper p-4">
                <h3 className="flex items-center gap-2 font-serif text-lg text-lc-ink">
                  <Key size={18} weight="duotone" className="text-lc-ocean" /> {t("md.hiwByoTitle")}
                </h3>
                <p className="mt-1 text-xs text-lc-steel">
                  {t("md.hiwByoIntro")}
                </p>
                <div className="mt-3 space-y-2">
                  {KEY_SOURCES.map((k) => (
                    <a key={k.name} href={k.url} target="_blank" rel="noreferrer"
                      data-testid={`keysource-${k.name.split(" ")[0].toLowerCase()}`}
                      className="group flex items-start gap-3 rounded-sm border border-lc-ink/10 bg-white p-3 hover:border-lc-ocean/40 transition-colors duration-200">
                      <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-sm bg-lc-navy">
                        <Lightning size={16} weight="fill" className="text-lc-sky" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-1.5 text-sm font-semibold text-lc-ink">
                          {k.name}
                          <ArrowSquareOut size={13} className="text-lc-steel group-hover:text-lc-ocean transition-colors duration-200" />
                        </div>
                        <p className="mt-0.5 text-[11px] leading-relaxed text-lc-steel">{t(k.noteKey)}</p>
                      </div>
                    </a>
                  ))}
                </div>
                <p className="mt-3 text-[11px] text-lc-steel">
                  {t("md.hiwByoOutro")}
                </p>
              </div>

              <div className="mt-6 rounded-sm border border-lc-warn/30 bg-lc-warn/5 p-3">
                <p className="text-[11px] leading-relaxed text-lc-steel">
                  {t("md.hiwDisclaimer")}
                </p>
              </div>
            </div>

            {onStartTour && (
              <div className="border-t border-lc-ink/10 p-4">
                <button onClick={onStartTour} data-testid="howitworks-start-tour"
                  className="inline-flex w-full items-center justify-center gap-2 rounded-sm bg-lc-navy px-5 py-3 text-sm font-semibold text-white hover:bg-lc-ocean transition-colors duration-200">
                  <Rocket size={16} weight="duotone" /> {t("md.hiwStartTour")}
                </button>
              </div>
            )}
          </motion.aside>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
